import { pointSegmentDistanceSquared } from "./collision";
import {
  add,
  clamp,
  distance,
  dot,
  fromAngle,
  magnitude,
  normalizeAngle,
  scale,
  subtract,
  vec,
  type Vec2,
} from "./math";

export interface Contact {
  readonly normal: Vec2;
  readonly penetration: number;
  readonly point: Vec2;
}

const normalizedOr = (value: Vec2, fallback: Vec2): Vec2 => {
  const length = magnitude(value);
  return length <= 1e-9 ? fallback : scale(value, 1 / length);
};

export const reflectHeading = (heading: number, normal: Vec2): number => {
  const direction = fromAngle(heading);
  const unitNormal = normalizedOr(normal, vec(-direction.x, -direction.y));
  const reflected = subtract(
    direction,
    scale(unitNormal, 2 * dot(direction, unitNormal)),
  );
  return normalizeAngle(Math.atan2(reflected.y, reflected.x));
};

export const circularBoundaryContact = (
  position: Vec2,
  radius: number,
  arenaRadius: number,
): Contact | null => {
  const centerDistance = distance(position, vec(0, 0));
  const penetration = centerDistance + radius - arenaRadius;
  if (penetration <= 0) return null;
  const normal = normalizedOr(position, vec(1, 0));
  return {
    normal,
    penetration,
    point: scale(normal, arenaRadius - radius),
  };
};

export const circleContact = (
  first: Vec2,
  firstRadius: number,
  second: Vec2,
  secondRadius: number,
  fallbackNormal = vec(1, 0),
): Contact | null => {
  const delta = subtract(first, second);
  const centerDistance = magnitude(delta);
  const penetration = firstRadius + secondRadius - centerDistance;
  if (penetration < 0) return null;
  const normal = normalizedOr(delta, fallbackNormal);
  return {
    normal,
    penetration,
    point: add(second, scale(normal, secondRadius)),
  };
};

export const circleCapsuleContact = (
  center: Vec2,
  circleRadius: number,
  start: Vec2,
  end: Vec2,
  capsuleRadius: number,
  fallbackNormal = vec(1, 0),
): Contact | null => {
  const segment = subtract(end, start);
  const lengthSquared = dot(segment, segment);
  const amount =
    lengthSquared <= Number.EPSILON
      ? 0
      : clamp(dot(subtract(center, start), segment) / lengthSquared, 0, 1);
  const closest = add(start, scale(segment, amount));
  const combinedRadius = circleRadius + capsuleRadius;
  if (pointSegmentDistanceSquared(center, start, end) > combinedRadius ** 2)
    return null;
  const delta = subtract(center, closest);
  const centerDistance = magnitude(delta);
  return {
    normal: normalizedOr(delta, fallbackNormal),
    penetration: Math.max(0, combinedRadius - centerDistance),
    point: closest,
  };
};

export const separatedPosition = (
  position: Vec2,
  contact: Contact,
  share = 1,
): Vec2 =>
  add(position, scale(contact.normal, (contact.penetration + 0.05) * share));
